import React from "react";

import {
  Button,
  H,
  Link,
  UrlTransformerProvider,
} from "../../../../../../wsui/base";
import HtmlProcessorExtensionProvider from "../../components/HtmlProcessorExtensionProvider";

import WpCaption from "./WpCaption.jsx";
import WpImage from "./WpImage.jsx";

const BUTTON_VARIANT_BY_CLASS = {
  "button--secondary": "secondary",
  "button--tertiary": "tertiary",
};

const BUTTON_STYLE_CLASSES = [
  "button",
  ...Object.keys(BUTTON_VARIANT_BY_CLASS),
];

function normalizeClassNames(className) {
  if (Array.isArray(className)) {
    return className.flatMap(normalizeClassNames);
  }
  if (typeof className !== "string") {
    return [];
  }
  return className.split(/\s+/).filter(Boolean);
}

function RichTextLink({ className, ...props }) {
  const classes = normalizeClassNames(className);

  if (!classes.includes("button")) {
    return <Link className={className} {...props} />;
  }

  const variant =
    classes
      .find((className) =>
        Object.prototype.hasOwnProperty.call(
          BUTTON_VARIANT_BY_CLASS,
          className,
        ),
      )
      ?.replace(/^button--/, "") || "primary";

  const remainingClassNames = classes.filter(
    (candidate) => !BUTTON_STYLE_CLASSES.includes(candidate),
  );

  // Let frontend variants control button appearance instead of WP editor inline styles.
  const { style: ignoredInlineStyle, ...buttonProps } = props;
  void ignoredInlineStyle;

  return (
    <Button
      variant={variant}
      className={remainingClassNames.length > 0 ? remainingClassNames : null}
      {...buttonProps}
    />
  );
}

export default function RootElementWrapper({ children }) {
  return (
    <HtmlProcessorExtensionProvider
      treeTransforms={[
        (
          tree,
          { visit, semanticHeadings, ensureHeadingIds, isHeadingElement, clsx },
        ) => {
          let baseHeadingLevel;
          let headingCount = 0;
          visit(tree, isHeadingElement(), (node) => {
            if (semanticHeadings) {
              let headingLevel = Number(node.tagName[1]);
              baseHeadingLevel = baseHeadingLevel || headingLevel;
              node.properties.className = clsx(
                node.properties.className,
                `wsui-h${headingLevel}`,
              );
              node.properties.adjustLevel = headingLevel - baseHeadingLevel;
              node.tagName = "heading";
            }
            if (ensureHeadingIds) {
              node.properties.id ??= `${
                typeof ensureHeadingIds === "string"
                  ? ensureHeadingIds
                  : "heading"
              }-${headingCount++}`;
            }
          });
        },
        (tree, { visit, contentMedia }) => {
          if (contentMedia) {
            visit(tree, { tagName: "img" }, (node) => {
              let attachmentId;
              if (node.properties && node.properties.className) {
                node.properties.className.some((className) => {
                  let matches = className.match(/^wp-image-(\d+)$/);
                  if (matches) {
                    attachmentId = matches[1];
                    return true;
                  }
                });
              }
              if (!attachmentId) {
                return;
              }
              node.tagName = "wp-image";
              node.properties = {
                ...node.properties,
                attachment: attachmentId,
                sizes: null,
              };
              // if (parent.tagName === "p") {
              //   parent.tagName = "div";
              //   parent.properties = {
              //     ...parent.properties,
              //     className: [
              //       ...((parent.properties && parent.properties.className) ||
              //         []),
              //       "paragraph",
              //     ],
              //   };
              // }
            });
          }
        },
      ]}
      stringifierComponents={{
        a: RichTextLink,
        heading: H,
        "wp-caption": WpCaption,
        "wp-image": WpImage,
      }}
    >
      <UrlTransformerProvider
        transformUrl={(url) => {
          url = url?.replace(/^http:/, "https:");
          url =
            process.env.GATSBY_WORDPRESS_UPLOADS_URL &&
            url?.startsWith(
              process.env.GATSBY_WORDPRESS_URL + "/wp-content/uploads/",
            )
              ? url.replace(
                  process.env.GATSBY_WORDPRESS_URL + "/wp-content/uploads",
                  process.env.GATSBY_WORDPRESS_UPLOADS_URL,
                )
              : url?.startsWith(process.env.GATSBY_WORDPRESS_URL + "/wp-")
              ? url
              : url?.replace(process.env.GATSBY_WORDPRESS_URL, "");
          return url;
        }}
      >
        {children}
      </UrlTransformerProvider>
    </HtmlProcessorExtensionProvider>
  );
}
